import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { AlertBanner, Button, EmptyState, Modal, SimpleChipItem } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { allTrackers } from "app/services/track/track";

import AddTagsCombobox from "../AddTagsCombobox";

import { TRACK_EVENT_NAME } from "./constants";
import { ChipsContainer, CurrentTagsSection } from "./styles";
import type { EditedTags, EditTagsModalProps, EditTagsWindowState } from "./types";

export type { EditedTags, EditTagsWindowState } from "./types";

export const EditTagsModal = ({ isOpen, onClose, selectedKeywords, allTags, onUpdateTags }: EditTagsModalProps) => {
    const { translate } = useTranslate();
    const [isComboboxOpen, setIsComboboxOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [pendingTagsToAdd, setPendingTagsToAdd] = useState<string[]>([]);
    const [pendingTagsToRemove, setPendingTagsToRemove] = useState<string[]>([]);

    const allTagsArray = useMemo(() => (allTags ? [...allTags] : []), [allTags]);

    /**
     * Tags assigned to passed keywords from table data, sorted by most used.
     */
    const assignedTags = useMemo(() => {
        const assignedTagsList: string[] = [];
        selectedKeywords.forEach((keywordData) => {
            keywordData.tags?.forEach((tag) => {
                if (!assignedTagsList.includes(tag.text)) {
                    assignedTagsList.push(tag.text);
                }
            });
        });
        // Filter allTagsArray to keep only assigned ones (preserves most-used order)
        const sortedTags = allTagsArray.filter((tag) => assignedTagsList.includes(tag));
        // Append newly created tags that aren't in allTagsArray
        const newTags = assignedTagsList.filter((tag) => !allTagsArray.includes(tag));
        return [...sortedTags, ...newTags];
    }, [selectedKeywords, allTagsArray]);

    /**
     * Filtered tags that we display in the current tags for selected keywords.
     */
    const visibleAssignedTags = useMemo(() => {
        return assignedTags.filter((tag) => !pendingTagsToRemove.includes(tag));
    }, [assignedTags, pendingTagsToRemove]);

    /**
     * Tags to add, displayed in the combobox options.
     */
    const visibleTagOptions = useMemo(() => {
        const tags = allTagsArray.filter((tag) => !pendingTagsToAdd.includes(tag));
        const lowerSearch = searchValue.toLowerCase().trim();
        if (!lowerSearch) {
            return tags;
        }
        return tags.filter((tag) => tag.toLowerCase().startsWith(lowerSearch));
    }, [allTagsArray, pendingTagsToAdd, searchValue]);

    const showCreateTagOption = useMemo(() => {
        const lowerSearch = searchValue.toLowerCase().trim();
        if (!lowerSearch) {
            return false;
        }
        const exactMatchExists = allTagsArray.some((tag) => tag.toLowerCase() === lowerSearch);
        const alreadyExists = visibleTagOptions.includes(searchValue) || pendingTagsToAdd.includes(searchValue);
        return !exactMatchExists && !alreadyExists;
    }, [visibleTagOptions, pendingTagsToAdd, searchValue, allTagsArray]);

    const handleAddTag = useCallback((tag: string) => {
        setPendingTagsToAdd((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
        setSearchValue("");
        setIsComboboxOpen(false);
    }, []);

    const handleAddedTagRemove = useCallback((tag: string) => {
        setPendingTagsToAdd((prev) => prev.filter((t) => t !== tag));
    }, []);

    const handleKeywordTagRemove = useCallback((tag: string) => {
        setPendingTagsToRemove((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
    }, []);

    const handleClose = useCallback(() => {
        setPendingTagsToAdd([]);
        setPendingTagsToRemove([]);
        setSearchValue("");
        setIsComboboxOpen(false);
        onClose();
    }, [onClose]);

    const isDirty = pendingTagsToAdd.length > 0 || pendingTagsToRemove.length > 0;

    const handleConfirm = useCallback(() => {
        const updatedData: EditedTags[] = selectedKeywords.map((row) => {
            const originalTags = row.tags?.map((tag) => tag.text) ?? [];

            // Remove tags
            const updatedTags = originalTags.filter((tag) => !pendingTagsToRemove.includes(tag));

            // Add tags (only if not already present)
            pendingTagsToAdd.forEach((tagToAdd) => {
                if (!updatedTags.includes(tagToAdd)) {
                    updatedTags.push(tagToAdd);
                }
            });

            return {
                keyword: row.keyword,
                tags: updatedTags,
            };
        });

        allTrackers.dispatchTrackEvent("Button", "submit-ok", `${TRACK_EVENT_NAME}/Apply`, {
            add: pendingTagsToAdd.length,
            remove: pendingTagsToRemove.length,
        });

        const editTagsWindowState: EditTagsWindowState = {
            updatedData,
            isDirty,
        };

        if (isDirty) {
            onUpdateTags(editTagsWindowState);
        }

        handleClose();
    }, [handleClose, isDirty, onUpdateTags, pendingTagsToAdd, pendingTagsToRemove, selectedKeywords]);

    return (
        <Modal
            isOpen={isOpen}
            customStyles={{ content: { width: "460px" } }}
            data-automation="edit-tags-modal"
        >
            <Modal.Header>
                <Modal.Title>{translate("rankTracker.keywordsReport.tags.action.button")}</Modal.Title>
            </Modal.Header>

            <Modal.Content
                padding="16px 24px 24px 24px"
                gap="20px"
            >
                <AddTagsCombobox
                    isOpen={isComboboxOpen}
                    onOpenChange={setIsComboboxOpen}
                    selectedChips={pendingTagsToAdd}
                    onCloseChip={handleAddedTagRemove}
                    searchValue={searchValue}
                    onSearchValueChange={setSearchValue}
                    options={visibleTagOptions}
                    showCreateOption={showCreateTagOption}
                    onOptionClick={handleAddTag}
                />

                <AlertBanner variant="info">
                    <AlertBanner.Image />
                    <AlertBanner.Description>
                        {translate("seo.keywordsCampaign.wizard.addingKeywordsStep.topBar.editKeywords.bannerTags")}
                    </AlertBanner.Description>
                </AlertBanner>

                {visibleAssignedTags.length > 0 ? (
                    <CurrentTagsSection>
                        <Text fontVariant="subtitle2">
                            {translate("rankTracker.editTags.modal.assignedTags.title")}
                        </Text>
                        <ChipsContainer>
                            {visibleAssignedTags.map((tag) => (
                                <SimpleChipItem
                                    key={tag}
                                    text={tag}
                                    onCloseItem={() => handleKeywordTagRemove(tag)}
                                />
                            ))}
                        </ChipsContainer>
                    </CurrentTagsSection>
                ) : (
                    <EmptyState
                        iconName="no-data-lab-2"
                        title={translate("rankTracker.editTags.modal.assignedTags.noData")}
                    />
                )}
            </Modal.Content>

            <Modal.Footer>
                <Button
                    type="flat"
                    onClick={() => {
                        allTrackers.dispatchTrackEvent("Button", "click", `${TRACK_EVENT_NAME}/Cancel`);
                        handleClose();
                    }}
                >
                    {translate("common.cancel")}
                </Button>
                <Button
                    isDisabled={!isDirty}
                    onClick={handleConfirm}
                >
                    {translate("global.confirm")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
